import { useEffect } from 'react'
import { Alert, Descriptions, Form, Input, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatMoneyValue } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { decidePurchase, type PurchaseRow } from './supplierApi'

export type PurchaseDecisionKind = 'submit' | 'verify' | 'cancel'

interface PurchaseDecisionModalProps {
  purchase: PurchaseRow | null
  decision: PurchaseDecisionKind
  onClose: () => void
}

export function PurchaseDecisionModal({ purchase, decision, onClose }: PurchaseDecisionModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<{ text?: string }>()
  const reasonRequired = decision === 'cancel'

  useEffect(() => {
    if (purchase) form.resetFields()
  }, [purchase, form])

  const submit = useMutation({
    mutationFn: (text: string | null) => decidePurchase(purchase?.id ?? '', decision, text),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.supplier.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      onClose()
    },
  })

  const close = () => {
    submit.reset()
    onClose()
  }

  return (
    <Modal
      open={purchase !== null}
      title={t(`purchase.decision.${decision}.title`)}
      okText={t(`purchase.decision.${decision}.submit`)}
      okButtonProps={{ danger: decision === 'cancel' }}
      cancelText={decision === 'cancel' ? t('common.back') : t('common.cancel')}
      confirmLoading={submit.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => submit.mutate(values.text?.trim() ? values.text.trim() : null))}
      destroyOnHidden
    >
      <Alert
        type={decision === 'cancel' ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 16 }}
        message={t(`purchase.decision.${decision}.warning`)}
      />
      {submit.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(submit.error, t)} />
      ) : null}
      {purchase ? (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('purchase.field.number')}>{purchase.number}</Descriptions.Item>
          <Descriptions.Item label={t('purchase.field.supplier')}>{purchase.supplier.name}</Descriptions.Item>
          <Descriptions.Item label={t('purchase.field.total')}>{formatMoneyValue(purchase.total)}</Descriptions.Item>
        </Descriptions>
      ) : null}
      {decision === 'submit' ? null : (
        <Form form={form} layout="vertical" preserve={false} disabled={submit.isPending}>
          <Form.Item
            name="text"
            label={t(reasonRequired ? 'purchase.decision.reason' : 'purchase.decision.note')}
            rules={reasonRequired ? [{ required: true, whitespace: true, message: t('purchase.validation.reason') }] : []}
          >
            <Input.TextArea rows={3} maxLength={500} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
